/**
 * v1 `weather/sensors` adapter.
 *
 * Payload shape (verified on staging 2026-05-15): bare JSON array of
 * station rows — no envelope. `weather/sensors` is the *union* feed
 * (MSC + Alberta511 + Netatmo, ~200+ stations). The per-source topics
 * `weather/metar`, `weather/alberta511`, `weather/netatmo` are subsets
 * of the same rows; we ignore them.
 *
 * Per-row schema quirks:
 *   - Alberta511 publishes Temperature/WindSpeed/Humidity as strings
 *     ("16.9"); MSC + Netatmo publish numbers. `numLike` coerces both.
 *   - Pressure lives under `Pressure` for Alberta511/Netatmo and
 *     `MeanSeaLevelPressure` for MSC METARs.
 *   - `WindSpeed` is km/h (Canadian convention — verified against
 *     known stations). UI conversion to mph happens in the consumer.
 *   - `LastUpdated` is unix epoch seconds. Null on stations that never
 *     reported.
 *   - `Camera_url` only present on Alberta511 (highway cameras).
 *
 * Stations missing Latitude/Longitude/Station_name/Data_source are
 * dropped — they can't be placed on the map or labelled.
 */
import { z } from 'zod';
import type { WeatherEnvelope, WxSensorRecord, WxSource } from '@/lib/mqtt/schemas/weather';

const numLike = z
  .union([z.number(), z.string()])
  .nullable()
  .optional()
  .transform((v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

/** ECCC MSC stations publish pressure in kPa (~100 range); Netatmo
 *  publishes in hPa (~1000 range); Alberta511 doesn't ship pressure.
 *  No unit field on the wire, so heuristic-normalise: anything below
 *  500 is treated as kPa and multiplied by 10 to land in hPa. */
function normalisePressureHpa(v: number | null): number | null {
  if (v == null) return null;
  return v < 500 ? v * 10 : v;
}

const V1WxStation = z
  .object({
    Id: z.union([z.string(), z.number()]).optional(),
    Station_name: z.string().nullish(),
    Latitude: z.number().nullish(),
    Longitude: z.number().nullish(),
    Data_source: z.enum(['MSC', 'Alberta511', 'Netatmo']).nullish(),
    LastUpdated: z.number().nullish(),
    Temperature: numLike,
    WindSpeed: numLike,
    WindAngle: numLike,
    WindDirection: z.string().nullish(),
    Humidity: numLike,
    Pressure: numLike,
    MeanSeaLevelPressure: numLike,
    DewPoint: numLike,
    Visibility: numLike,
    Camera_url: z.string().nullish(),
    Location: z.string().nullish(),
    Roadway: z.string().nullish(),
  })
  .passthrough();

const V1WeatherSensorsPayload = z.array(V1WxStation);

export function weatherSensorsToEnvelope(raw: unknown): WeatherEnvelope | null {
  const parsed = V1WeatherSensorsPayload.safeParse(raw);
  if (!parsed.success) return null;
  const stations: WxSensorRecord[] = [];
  for (const r of parsed.data) {
    if (r.Id == null) continue;
    if (!r.Station_name) continue;
    if (r.Latitude == null || r.Longitude == null) continue;
    if (!r.Data_source) continue;
    const source: WxSource = r.Data_source;
    const observed_at = r.LastUpdated != null ? new Date(r.LastUpdated * 1000).toISOString() : null;
    const pressure_hpa = normalisePressureHpa(r.Pressure ?? r.MeanSeaLevelPressure ?? null);
    stations.push({
      id: String(r.Id),
      station_name: r.Station_name,
      source,
      lat: r.Latitude,
      lng: r.Longitude,
      observed_at,
      temp_c: r.Temperature,
      wind_speed_kmh: r.WindSpeed,
      wind_dir_deg: r.WindAngle,
      wind_dir_card: r.WindDirection ?? null,
      humidity_pct: r.Humidity,
      pressure_hpa,
      dew_point_c: r.DewPoint,
      visibility_m: r.Visibility,
      camera_url: r.Camera_url ?? null,
      location: r.Location ?? null,
      roadway: r.Roadway ?? null,
    });
  }
  return {
    stations,
    received_at: new Date().toISOString(),
  };
}
